/* ==========================================================================
   Matthews Cleaning Co. — Product Detail Page JS
   Handles: gallery, tier pricing, qty selector, cart intercept,
            shipping bar, FBT, recently viewed, sticky ATC, dispatch countdown
   Single IIFE, vanilla JS, no dependencies.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Find the section root ---------- */
  var root = document.querySelector('.mcc-pdp');
  if (!root) return;

  /* ---------- Config from data attrs ---------- */
  var cfg = {
    sectionId: root.dataset.sectionId,
    productId: parseInt(root.dataset.productId, 10) || 0,
    productHandle: root.dataset.productHandle || '',
    productTitle: root.dataset.productTitle || '',
    variantId: parseInt(root.dataset.variantId, 10) || 0,
    basePrice: parseInt(root.dataset.basePrice, 10) || 0,
    bulkPrice: parseInt(root.dataset.bulkPrice, 10) || 0,
    maxPrice: parseInt(root.dataset.maxPrice, 10) || 0,
    bulkQty: parseInt(root.dataset.bulkQty, 10) || 3,
    maxQty: parseInt(root.dataset.maxQty, 10) || 10,
    bulkDiscount: parseInt(root.dataset.bulkDiscount, 10) || 5,
    maxDiscount: parseInt(root.dataset.maxDiscount, 10) || 10,
    moq: parseInt(root.dataset.moq, 10) || 1,
    defaultQty: parseInt(root.dataset.defaultQty, 10) || 1,
    shippingThreshold: parseInt(root.dataset.shippingThreshold, 10) || 12500,
    dispatchCutoffHour: parseInt(root.dataset.dispatchCutoff, 10) || 14,
    bulkEnabled: root.dataset.bulkEnabled === 'true',
    primaryCollectionHandle: root.dataset.primaryCollectionHandle || '',
    showStickyAtc: root.dataset.showStickyAtc === 'true',
    showFbt: root.dataset.showFbt === 'true',
    showRelated: root.dataset.showRelated === 'true',
    showRecentlyViewed: root.dataset.showRecentlyViewed === 'true',
    showDispatchCountdown: root.dataset.showDispatchCountdown === 'true',
    showShippingBar: root.dataset.showShippingBar === 'true',
    unitLabel: root.dataset.unitLabel || 'units'
  };

  /* ---------- Money formatting ---------- */
  function formatMoney(cents) {
    if (isNaN(cents)) cents = 0;
    return '$' + (cents / 100).toFixed(2);
  }

  function splitMoney(cents) {
    if (isNaN(cents)) cents = 0;
    var dollars = Math.floor(cents / 100);
    var remainder = cents - (dollars * 100);
    var centsStr = remainder < 10 ? '0' + remainder : '' + remainder;
    return { dollars: '$' + dollars, cents: '.' + centsStr };
  }

  /* ---------- Tier detection ---------- */
  function getTierForQty(qty) {
    if (qty >= cfg.maxQty) return 'max';
    if (qty >= cfg.bulkQty) return 'popular';
    return 'single';
  }

  function getEffectivePricePerUnit(qty) {
    if (!cfg.bulkEnabled) return cfg.basePrice;
    var tier = getTierForQty(qty);
    if (tier === 'max') return cfg.maxPrice;
    if (tier === 'popular') return cfg.bulkPrice;
    return cfg.basePrice;
  }

  /* ---------- Qty input ---------- */
  var qtyInput = root.querySelector('[data-mcc-qty-input]');
  var qtyMinusBtn = root.querySelector('[data-mcc-qty-minus]');
  var qtyPlusBtn = root.querySelector('[data-mcc-qty-plus]');

  function getQty() {
    if (!qtyInput) return cfg.defaultQty;
    var v = parseInt(qtyInput.value, 10);
    if (isNaN(v) || v < cfg.moq) return cfg.moq;
    if (v > 999) return 999;
    return v;
  }

  function setQty(newQty) {
    if (!qtyInput) return;
    if (newQty < cfg.moq) newQty = cfg.moq;
    if (newQty > 999) newQty = 999;
    qtyInput.value = newQty;
    updateAll(newQty);
  }

  function updateAll(qty) {
    if (typeof qty === 'undefined') qty = getQty();
    if (qtyMinusBtn) qtyMinusBtn.disabled = qty <= cfg.moq;
    updateTierDisplay(qty);
    updateNudge(qty);
    updateShippingBar();
    updateStickyBar(qty);
  }

  if (qtyMinusBtn) {
    qtyMinusBtn.addEventListener('click', function () { setQty(getQty() - 1); });
  }
  if (qtyPlusBtn) {
    qtyPlusBtn.addEventListener('click', function () { setQty(getQty() + 1); });
  }
  if (qtyInput) {
    qtyInput.addEventListener('input', function () { updateAll(getQty()); });
    qtyInput.addEventListener('change', function () { setQty(getQty()); });
  }

  /* ---------- Tier cards ---------- */
  var tierButtons = root.querySelectorAll('[data-mcc-tier]');

  tierButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var tier = btn.dataset.mccTier;
      var targetQty = tier === 'single' ? cfg.moq :
                      tier === 'popular' ? cfg.bulkQty :
                      cfg.maxQty;
      setQty(targetQty);
    });
  });

  function updateTierDisplay(qty) {
    var activeTier = getTierForQty(qty);
    tierButtons.forEach(function (btn) {
      if (btn.dataset.mccTier === activeTier) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  /* ---------- Nudge banner ---------- */
  var nudgeEl = root.querySelector('[data-mcc-nudge]');
  var nudgeTextEl = root.querySelector('[data-mcc-nudge-text]');

  function updateNudge(qty) {
    if (!nudgeEl || !nudgeTextEl || !cfg.bulkEnabled) {
      if (nudgeEl) nudgeEl.classList.add('is-hidden');
      return;
    }
    var tier = getTierForQty(qty);
    if (tier === 'max') {
      nudgeEl.classList.add('is-hidden');
    } else if (tier === 'popular') {
      var needed = cfg.maxQty - qty;
      var nextSaving = (cfg.basePrice - cfg.maxPrice) * cfg.maxQty;
      nudgeEl.classList.remove('is-hidden');
      nudgeTextEl.innerHTML =
        'Add <strong>' + needed + '</strong> more ' + (needed === 1 ? cfg.unitLabel : cfg.unitLabel + 's') +
        ' → qty ' + cfg.maxQty + ' → unlock <strong>' + cfg.maxDiscount + '% off</strong> (save ' +
        formatMoney(nextSaving) + ')';
    } else {
      var needed2 = cfg.bulkQty - qty;
      var nextSaving2 = (cfg.basePrice - cfg.bulkPrice) * cfg.bulkQty;
      nudgeEl.classList.remove('is-hidden');
      nudgeTextEl.innerHTML =
        'Add <strong>' + needed2 + '</strong> more ' + (needed2 === 1 ? cfg.unitLabel : cfg.unitLabel + 's') +
        ' → qty ' + cfg.bulkQty + ' → unlock <strong>' + cfg.bulkDiscount + '% off</strong> (save ' +
        formatMoney(nextSaving2) + ')';
    }
  }

  /* ---------- Shipping bar ---------- */
  var shippingBar = root.querySelector('[data-mcc-shipping-bar]');
  var shippingFill = root.querySelector('[data-mcc-shipping-fill]');
  var shippingMsg = root.querySelector('[data-mcc-shipping-msg]');
  var shippingBadge = root.querySelector('[data-mcc-shipping-badge]');
  var cartTotal = 0;
  var shippingWasSuccess = false;

  function fetchCartTotal() {
    if (!window.fetch) return Promise.resolve(0);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return cart.total_price || 0; })
      .catch(function () { return 0; });
  }

  function updateShippingBar() {
    if (!shippingBar || !shippingFill || !shippingMsg) return;
    var qty = getQty();
    var pricePerUnit = getEffectivePricePerUnit(qty);
    var lineTotal = pricePerUnit * qty;
    var projected = cartTotal + lineTotal;
    var pct = Math.min(100, (projected / cfg.shippingThreshold) * 100);

    shippingFill.style.width = pct + '%';

    if (projected >= cfg.shippingThreshold) {
      shippingBar.classList.add('mcc-pdp__shipping-bar--success');
      shippingMsg.innerHTML = '<strong>Free shipping unlocked</strong>';
      if (shippingBadge) shippingBadge.textContent = formatMoney(projected) + ' / ' + formatMoney(cfg.shippingThreshold);
      if (!shippingWasSuccess) {
        fireConfetti(shippingBar);
        shippingWasSuccess = true;
      }
    } else {
      shippingBar.classList.remove('mcc-pdp__shipping-bar--success');
      shippingWasSuccess = false;
      var remaining = cfg.shippingThreshold - projected;
      shippingMsg.innerHTML = 'Spend <strong>' + formatMoney(remaining) +
        '</strong> more for <strong>FREE shipping</strong>';
      if (shippingBadge) shippingBadge.textContent = formatMoney(projected) + ' / ' + formatMoney(cfg.shippingThreshold);
    }
  }

  /* ---------- Confetti ---------- */
  function fireConfetti(container) {
    var existing = container.querySelector('.mcc-pdp__confetti');
    if (existing) existing.remove();
    var wrap = document.createElement('div');
    wrap.className = 'mcc-pdp__confetti';
    var colors = ['#40b4e2', '#071429', '#f0493e', '#5dc4ea', '#1a8ab5', '#fbbf24'];
    for (var i = 0; i < 32; i++) {
      var piece = document.createElement('div');
      piece.className = 'mcc-pdp__confetti-piece';
      piece.style.setProperty('--mcc-x', ((Math.random() - 0.5) * 80) + 'px');
      piece.style.left = Math.random() * 100 + '%';
      piece.style.top = -(Math.random() * 5) + 'px';
      piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      piece.style.animationDelay = (Math.random() * 0.6) + 's';
      piece.style.animationDuration = (1.5 + Math.random() * 0.8) + 's';
      wrap.appendChild(piece);
    }
    container.appendChild(wrap);
    setTimeout(function () { wrap.remove(); }, 3000);
  }

  /* ---------- Image gallery ---------- */
  var galleryMainImg = root.querySelector('[data-mcc-gallery-main]');
  var galleryThumbs = root.querySelectorAll('[data-mcc-gallery-thumb]');
  var galleryDots = root.querySelectorAll('[data-mcc-gallery-dot]');
  var galleryCurrentEl = root.querySelector('[data-mcc-gallery-current]');
  var galleryContainer = root.querySelector('[data-mcc-gallery-container]');
  var galleryNavPrev = root.querySelector('[data-mcc-gallery-prev]');
  var galleryNavNext = root.querySelector('[data-mcc-gallery-next]');
  var galleryIndex = 0;
  var galleryTotal = galleryThumbs.length;

  function showImage(index) {
    if (!galleryMainImg || index < 0 || index >= galleryTotal) return;
    var thumb = galleryThumbs[index];
    var imgUrl = thumb.dataset.imageUrl;
    if (!imgUrl) return;

    galleryMainImg.classList.add('is-loading');
    // The first photo loads through srcset (smaller files on phones). Drop it so the
    // chosen thumbnail's image (imgUrl) is the one that displays.
    galleryMainImg.removeAttribute('srcset');
    galleryMainImg.removeAttribute('sizes');
    galleryMainImg.src = imgUrl;
    // onload reset
    galleryMainImg.onload = function () { galleryMainImg.classList.remove('is-loading'); };

    galleryThumbs.forEach(function (t, i) {
      // aria-current tells screen readers which photo is showing (7 Oct 2026)
      if (i === index) { t.classList.add('is-active'); t.setAttribute('aria-current', 'true'); }
      else { t.classList.remove('is-active'); t.removeAttribute('aria-current'); }
    });
    galleryDots.forEach(function (d, i) {
      if (i === index) d.classList.add('is-active'); else d.classList.remove('is-active');
    });
    if (galleryCurrentEl) galleryCurrentEl.textContent = index + 1;
    galleryIndex = index;

    // preload neighbours
    [index - 1, index + 1].forEach(function (i) {
      if (i >= 0 && i < galleryTotal) {
        var next = galleryThumbs[i].dataset.imageUrl;
        if (next) { var img = new Image(); img.src = next; }
      }
    });
  }

  galleryThumbs.forEach(function (t, i) {
    t.addEventListener('click', function () { showImage(i); });
  });

  if (galleryNavPrev) {
    galleryNavPrev.addEventListener('click', function () {
      showImage(galleryIndex === 0 ? galleryTotal - 1 : galleryIndex - 1);
    });
  }
  if (galleryNavNext) {
    galleryNavNext.addEventListener('click', function () {
      showImage(galleryIndex === galleryTotal - 1 ? 0 : galleryIndex + 1);
    });
  }

  // Keyboard arrows
  document.addEventListener('keydown', function (e) {
    if (document.activeElement && /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) return;
    if (galleryTotal <= 1) return;
    if (e.key === 'ArrowLeft') {
      showImage(galleryIndex === 0 ? galleryTotal - 1 : galleryIndex - 1);
    } else if (e.key === 'ArrowRight') {
      showImage(galleryIndex === galleryTotal - 1 ? 0 : galleryIndex + 1);
    }
  });

  // Touch swipe
  if (galleryContainer) {
    var touchStartX = 0;
    galleryContainer.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    galleryContainer.addEventListener('touchend', function (e) {
      var diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > 50 && galleryTotal > 1) {
        if (diff > 0) showImage(galleryIndex === galleryTotal - 1 ? 0 : galleryIndex + 1);
        else showImage(galleryIndex === 0 ? galleryTotal - 1 : galleryIndex - 1);
      }
    }, { passive: true });
  }

  /* ---------- Cart intercept ---------- */
  // Monkey-patch window.fetch to inject our qty whenever Dawn's buy-buttons
  // submits a /cart/add request. Only the main product form (FormData) takes
  // the page quantity. JSON adds from the related, recently viewed and add-on
  // cards keep their own quantity (7 Oct 2026).
  var originalFetch = window.fetch;
  window.fetch = function (url, options) {
    if (typeof url === 'string' && url.indexOf('/cart/add') !== -1 && options && options.body) {
      try {
        if (options.body instanceof FormData) {
          options.body.set('quantity', getQty());
        }
      } catch (err) { /* swallow */ }
    }
    var result = originalFetch.apply(this, arguments);

    // Re-fetch cart total after any successful /cart/add
    if (typeof url === 'string' && url.indexOf('/cart/') !== -1) {
      result.then(function () {
        setTimeout(function () {
          fetchCartTotal().then(function (t) {
            cartTotal = t;
            updateShippingBar();
          });
        }, 150);
      }).catch(function () {});
    }
    return result;
  };

  /* ---------- Sticky ATC bar ---------- */
  var stickyBar = root.querySelector('[data-mcc-sticky]');
  var stickyQtyInput = root.querySelector('[data-mcc-sticky-qty-input]');
  var stickyQtyMinus = root.querySelector('[data-mcc-sticky-qty-minus]');
  var stickyQtyPlus = root.querySelector('[data-mcc-sticky-qty-plus]');
  var stickyTotal = root.querySelector('[data-mcc-sticky-total]');
  var stickySub = root.querySelector('[data-mcc-sticky-sub]');
  var stickyCta = root.querySelector('[data-mcc-sticky-cta]');
  var buyBox = root.querySelector('[data-mcc-buybox]');

  if (cfg.showStickyAtc && stickyBar && buyBox) {
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            stickyBar.classList.add('is-visible');
            stickyBar.removeAttribute('aria-hidden');
            stickyBar.inert = false;
            root.classList.add('has-sticky-atc');
          } else {
            stickyBar.classList.remove('is-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
            stickyBar.inert = true;
            root.classList.remove('has-sticky-atc');
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
      observer.observe(buyBox);
    }

    if (stickyQtyMinus) stickyQtyMinus.addEventListener('click', function () { setQty(getQty() - 1); });
    if (stickyQtyPlus) stickyQtyPlus.addEventListener('click', function () { setQty(getQty() + 1); });
    if (stickyQtyInput) {
      stickyQtyInput.addEventListener('change', function () {
        var v = parseInt(stickyQtyInput.value, 10);
        if (!isNaN(v)) setQty(v);
      });
    }
    if (stickyCta) {
      stickyCta.addEventListener('click', function () {
        // Trigger main ATC button
        var mainSubmit = root.querySelector('.mcc-pdp__buy-buttons .product-form__submit');
        if (mainSubmit) mainSubmit.click();
      });
    }
  }

  function updateStickyBar(qty) {
    if (!stickyBar) return;
    if (stickyQtyInput) stickyQtyInput.value = qty;
    var pricePerUnit = getEffectivePricePerUnit(qty);
    var lineTotal = pricePerUnit * qty;
    if (stickyTotal) stickyTotal.textContent = formatMoney(lineTotal);

    if (stickySub) {
      var tier = getTierForQty(qty);
      if (cfg.bulkEnabled && tier === 'popular') {
        stickySub.textContent = 'Qty ' + qty + ' · ' + cfg.bulkDiscount + '% off applied';
      } else if (cfg.bulkEnabled && tier === 'max') {
        stickySub.textContent = 'Qty ' + qty + ' · ' + cfg.maxDiscount + '% off applied';
      } else {
        stickySub.textContent = 'Qty ' + qty;
      }
    }
  }

  /* ---------- Dispatch countdown ---------- */
  var dispatchEl = root.querySelector('[data-mcc-dispatch]');

  function updateDispatchCountdown() {
    if (!dispatchEl || !cfg.showDispatchCountdown) return;

    // Work out time remaining until cutoff in Sydney (AEST/AEDT aware)
    // Use Intl.DateTimeFormat to get the current hour/minute in Australia/Sydney
    try {
      var now = new Date();
      var sydney = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
      var dayOfWeek = sydney.getDay(); // 0=Sun, 6=Sat
      var hour = sydney.getHours();
      var minute = sydney.getMinutes();
      var second = sydney.getSeconds();

      // If weekend, show Monday dispatch
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        dispatchEl.innerHTML =
          '<svg class="mcc-pdp__trust-icon" viewBox="0 0 16 16"><path fill="currentColor" d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5A5.5 5.5 0 1113.5 8 5.5 5.5 0 018 13.5zM8 4a.75.75 0 01.75.75V8l2.5 1.5a.75.75 0 01-.8 1.28L7.6 9.13a.75.75 0 01-.35-.63V4.75A.75.75 0 018 4z"/></svg>' +
          '<span>Dispatches <strong>Monday</strong> · order now to be first in the queue</span>';
        return;
      }

      // If after cutoff, show next-day dispatch
      if (hour >= cfg.dispatchCutoffHour) {
        var nextDay = dayOfWeek === 5 ? 'Monday' : 'tomorrow';
        dispatchEl.innerHTML =
          '<svg class="mcc-pdp__trust-icon" viewBox="0 0 16 16"><path fill="currentColor" d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5A5.5 5.5 0 1113.5 8 5.5 5.5 0 018 13.5zM8 4a.75.75 0 01.75.75V8l2.5 1.5a.75.75 0 01-.8 1.28L7.6 9.13a.75.75 0 01-.35-.63V4.75A.75.75 0 018 4z"/></svg>' +
          '<span>Dispatches <strong>' + nextDay + '</strong> · cutoff was ' + cfg.dispatchCutoffHour + ':00 Sydney time</span>';
        return;
      }

      // Before cutoff: show countdown
      var remainingMin = ((cfg.dispatchCutoffHour - hour) * 60) - minute;
      var remainingSec = 60 - second;
      if (remainingSec === 60) { remainingSec = 0; } else { remainingMin -= 1; }
      var hrs = Math.floor(remainingMin / 60);
      var mins = remainingMin % 60;

      var countdownStr;
      if (hrs > 0) {
        countdownStr = hrs + 'h ' + mins + 'm';
      } else if (mins > 5) {
        countdownStr = mins + 'm';
      } else {
        countdownStr = mins + 'm ' + remainingSec + 's';
      }

      dispatchEl.innerHTML =
        '<svg class="mcc-pdp__trust-icon" viewBox="0 0 16 16"><path fill="currentColor" d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5A5.5 5.5 0 1113.5 8 5.5 5.5 0 018 13.5zM8 4a.75.75 0 01.75.75V8l2.5 1.5a.75.75 0 01-.8 1.28L7.6 9.13a.75.75 0 01-.35-.63V4.75A.75.75 0 018 4z"/></svg>' +
        '<span>Order within <em>' + countdownStr + '</em> for same-day dispatch</span>';
    } catch (err) {
      // Timezone handling not available — hide countdown gracefully
      if (dispatchEl) dispatchEl.style.display = 'none';
    }
  }

  /* ---------- Frequently Bought Together ---------- */
  var fbtContainer = root.querySelector('[data-mcc-fbt]');

  function renderFbt(products) {
    if (!fbtContainer) return;

    // No products → hide entire FBT section
    if (!products || !products.length) {
      fbtContainer.style.display = 'none';
      return;
    }

    // Has products → show container and render content
    fbtContainer.style.display = 'block';

    // Show up to 2 recommendations
    var recs = products.slice(0, 2);

    var selected = { 0: true, 1: true };  // both recs start selected

    function computeBundleTotal() {
      var total = getEffectivePricePerUnit(getQty()) * getQty();
      recs.forEach(function (p, i) {
        if (selected[i]) total += p.price || 0;
      });
      return total;
    }

    function computeSavings() {
      // No actual bundle discount exists; show a token saving if both add-ons selected
      // (in practice Shopify doesn't discount FBT — just shows "stack the order" value)
      return 0;
    }

    var thisImg = root.querySelector('[data-mcc-hero-img]') ?
      root.querySelector('[data-mcc-hero-img]').src : '';

    var html = '<div class="mcc-pdp__section-header">' +
                 '<h2 class="mcc-pdp__section-title">Frequently bought together</h2>' +
               '</div>' +
               '<div class="mcc-pdp__fbt-grid">' +
                 '<div class="mcc-pdp__fbt-card mcc-pdp__fbt-card--primary">' +
                   '<span class="mcc-pdp__fbt-card-badge">This item</span>' +
                   '<img class="mcc-pdp__fbt-card-img" src="' + thisImg + '" alt="">' +
                   '<p class="mcc-pdp__fbt-card-title">' + cfg.productTitle + '</p>' +
                   '<p class="mcc-pdp__fbt-card-price">' + formatMoney(cfg.basePrice) + '</p>' +
                   '<div class="mcc-pdp__fbt-card-action"><span class="mcc-pdp__fbt-toggle is-selected">✓ Added</span></div>' +
                 '</div>';

    recs.forEach(function (p, i) {
      var imgUrl = p.featured_image || (p.media && p.media[0] && p.media[0].src) || '';
      html += '<div class="mcc-pdp__fbt-card" data-fbt-rec-index="' + i + '">' +
                '<img class="mcc-pdp__fbt-card-img" src="' + imgUrl + '" alt="' +
                    (p.title || '').replace(/"/g, '&quot;') + '">' +
                '<a class="mcc-pdp__fbt-card-title" href="/products/' + p.handle + '">' +
                    p.title + '</a>' +
                '<p class="mcc-pdp__fbt-card-price">' + formatMoney(p.price) + '</p>' +
                '<div class="mcc-pdp__fbt-card-action">' +
                  '<button type="button" class="mcc-pdp__fbt-toggle is-selected" data-fbt-toggle="' + i + '">✓ In bundle</button>' +
                '</div>' +
              '</div>';
    });

    // Bundle summary card
    html += '<div class="mcc-pdp__fbt-summary">' +
              '<p class="mcc-pdp__fbt-summary-label">Bundle total</p>' +
              '<p class="mcc-pdp__fbt-summary-total" data-fbt-total>' + formatMoney(computeBundleTotal()) + '</p>' +
              '<button type="button" class="mcc-pdp__fbt-summary-cta" data-fbt-add-all>Add all</button>' +
              '<p class="mcc-pdp__fbt-summary-hint">Counts towards free shipping</p>' +
            '</div>';

    html += '</div>';

    fbtContainer.innerHTML = html;

    // Wire toggles
    fbtContainer.querySelectorAll('[data-fbt-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.dataset.fbtToggle, 10);
        selected[idx] = !selected[idx];
        btn.classList.toggle('is-selected');
        btn.textContent = selected[idx] ? '✓ In bundle' : '+ Add bundle';
        var totalEl = fbtContainer.querySelector('[data-fbt-total]');
        if (totalEl) totalEl.textContent = formatMoney(computeBundleTotal());
      });
    });

    // Wire "add all"
    var addAllBtn = fbtContainer.querySelector('[data-fbt-add-all]');
    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        addAllBtn.disabled = true;
        addAllBtn.textContent = 'Adding…';

        // Build cart add payload
        var items = [{ id: cfg.variantId, quantity: getQty() }];
        recs.forEach(function (p, i) {
          if (selected[i] && p.variants && p.variants[0]) {
            items.push({ id: p.variants[0].id, quantity: 1 });
          }
        });

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
        })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          // Open the slide-out cart when the theme has one (7 Oct 2026)
          var drawer = document.querySelector('cart-drawer');
          if (drawer && json && json.sections && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            drawer.renderContents(json);
            addAllBtn.disabled = false;
            addAllBtn.textContent = 'Add all';
            return;
          }
          window.location.href = '/cart';
        })
        .catch(function () {
          addAllBtn.disabled = false;
          addAllBtn.textContent = 'Add all';
        });
      });
    }
  }

  function fetchFbt() {
    if (!cfg.showFbt || !cfg.productId || !fbtContainer) return;
    var url = '/recommendations/products.json?product_id=' + cfg.productId + '&intent=complementary&limit=2';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { renderFbt(data.products || []); })
      .catch(function () { renderFbt([]); });
  }

  /* ---------- Recently viewed ---------- */
  var recentlyContainer = root.querySelector('[data-mcc-recently-viewed]');
  var RECENT_KEY = 'mcc_recently_viewed';
  var RECENT_MAX = 6;

  function getRecentlyViewed() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (err) { return []; }
  }

  function saveRecentlyViewed(arr) {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_MAX))); }
    catch (err) { /* quota or disabled */ }
  }

  function trackCurrent() {
    if (!cfg.productHandle) return;
    var current = getRecentlyViewed();
    // dedupe
    current = current.filter(function (h) { return h !== cfg.productHandle; });
    current.unshift(cfg.productHandle);
    saveRecentlyViewed(current);
  }

  function renderRecentlyViewed() {
    if (!recentlyContainer || !cfg.showRecentlyViewed) return;

    var handles = getRecentlyViewed().filter(function (h) { return h !== cfg.productHandle; });
    if (!handles.length) {
      recentlyContainer.style.display = 'none';
      return;
    }
    handles = handles.slice(0, 6);

    // Fetch all in parallel
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + h + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var valid = products.filter(function (p) { return p && p.handle; });
      if (!valid.length) {
        recentlyContainer.style.display = 'none';
        return;
      }

      var heading = recentlyContainer.dataset.heading || 'Recently viewed';
      var html = '<div class="mcc-pdp__section-header">' +
                   '<h2 class="mcc-pdp__section-title">' + heading + '</h2>' +
                 '</div>' +
                 '<div class="mcc-pdp__card-row mcc-pdp__card-row--recent">';
      valid.forEach(function (p) {
        var imgUrl = p.featured_image || (p.images && p.images[0]) || '';
        var escapedTitle = (p.title || '').replace(/"/g, '&quot;');
        var eyebrowRaw = p.type || (p.tags && p.tags[0]) || '';
        var eyebrow = String(eyebrowRaw)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

        html += '<div class="mcc-pdp__card">' +
                  '<a class="mcc-pdp__card-link" href="/products/' + p.handle + '">' +
                    '<img class="mcc-pdp__card-img" src="' + imgUrl + '" alt="' + escapedTitle + '" loading="lazy" width="400">' +
                    (eyebrow ? '<p class="mcc-pdp__card-eyebrow">' + eyebrow + '</p>' : '') +
                    '<p class="mcc-pdp__card-title">' + p.title + '</p>' +
                  '</a>' +
                  '<p class="mcc-pdp__card-price">' + formatMoney(p.price) + '</p>';

        if (p.available === false) {
          html += '<button type="button" class="mcc-pdp__card-atc" disabled>Sold out</button>';
        } else if (p.variants && p.variants.length === 1) {
          html += '<button type="button" class="mcc-pdp__card-atc" data-mcc-quickadd ' +
                    'data-variant-id="' + p.variants[0].id + '" ' +
                    'data-product-title="' + escapedTitle + '">' +
                    'Add to cart' +
                  '</button>';
        } else {
          html += '<a class="mcc-pdp__card-atc mcc-pdp__card-atc--link" ' +
                    'href="/products/' + p.handle + '">' +
                    'Choose options \u2192' +
                  '</a>';
        }

        html += '</div>';
      });
      html += '</div>';
      recentlyContainer.innerHTML = html;
    });
  }

  /* ---------- Tab / accordion switching ---------- */
  var tabs = root.querySelectorAll('[data-mcc-tab]');
  var panels = root.querySelectorAll('[data-mcc-panel]');

  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      var target = tab.dataset.mccTab;
      tabs.forEach(function (t) { t.classList.remove('is-active'); });
      tab.classList.add('is-active');
      panels.forEach(function (p) {
        if (p.dataset.mccPanel === target) p.classList.add('is-active');
        else p.classList.remove('is-active');
      });
    });
  });

  // Mobile accordion headers (single-open: close siblings on open)
  root.querySelectorAll('[data-mcc-panel-header]').forEach(function (h) {
    h.addEventListener('click', function () {
      var panel = h.closest('[data-mcc-panel]');
      if (!panel) return;
      var willOpen = !panel.classList.contains('is-open');
      if (willOpen) {
        root.querySelectorAll('[data-mcc-panel].is-open').forEach(function (other) {
          if (other !== panel) other.classList.remove('is-open');
        });
      }
      panel.classList.toggle('is-open');
    });
  });

  /* ---------- Init ---------- */
  function init() {
    // Remove stray return_to hidden inputs from Dawn's form
    root.querySelectorAll('.mcc-pdp__buy-buttons input[name="return_to"]').forEach(function (el) {
      el.remove();
    });

    // Inject hidden quantity input into Dawn's form so native submission
    // also carries qty, not just AJAX submission (belt-and-braces for cart add)
    var dawnForm = root.querySelector('.mcc-pdp__buy-buttons form');
    if (dawnForm && !dawnForm.querySelector('input[name="quantity"]')) {
      var hiddenQty = document.createElement('input');
      hiddenQty.type = 'hidden';
      hiddenQty.name = 'quantity';
      hiddenQty.value = getQty();
      dawnForm.appendChild(hiddenQty);

      // Keep the hidden input in sync with qty changes via setQty()
      var originalSetQty = setQty;
      setQty = function (newQty) {
        originalSetQty(newQty);
        hiddenQty.value = getQty();
      };
      
      // Also sync on direct input changes
      if (qtyInput) {
        qtyInput.addEventListener('input', function () {
          hiddenQty.value = getQty();
        });
      }
    }

    /* Quick-add handler for "More in" + Recently viewed cards.
       Uses event delegation so it works for both Liquid-rendered
       and JS-rendered cards. */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mcc-quickadd]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      var originalLabel = btn.textContent;
      btn.classList.add('is-adding');
      btn.textContent = 'Adding\u2026';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Add failed');
        return r.json();
      })
      .then(function (json) {
        btn.classList.remove('is-adding');
        btn.classList.add('is-added');
        btn.textContent = '\u2713 Added';

        // Refresh cart total so shipping bar updates
        fetchCartTotal().then(function (t) {
          cartTotal = t;
          updateShippingBar();
        });

        // Open the slide-out cart with the updated contents (7 Oct 2026)
        var drawer = document.querySelector('cart-drawer');
        if (drawer && json && json.sections && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        }

        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.textContent = originalLabel;
          btn.disabled = false;
        }, 2000);
      })
      .catch(function () {
        btn.classList.remove('is-adding');
        btn.textContent = 'Error \u2014 try again';
        setTimeout(function () {
          btn.textContent = originalLabel;
          btn.disabled = false;
        }, 2500);
      });
    });

    // First tab active
    if (tabs.length > 0 && !root.querySelector('[data-mcc-tab].is-active')) {
      tabs[0].classList.add('is-active');
    }
    if (panels.length > 0 && !root.querySelector('[data-mcc-panel].is-active')) {
      panels[0].classList.add('is-active');
      // On mobile also open first accordion
      panels[0].classList.add('is-open');
    }

    // Initial state
    updateAll(cfg.defaultQty);

    // Shipping bar initial cart fetch
    if (cfg.showShippingBar) {
      fetchCartTotal().then(function (t) {
        cartTotal = t;
        updateShippingBar();
      });
    }

    // Track + render recently viewed (after a tick so we don't block)
    setTimeout(function () {
      trackCurrent();
      renderRecentlyViewed();
    }, 100);

    // FBT
    if (cfg.showFbt) {
      setTimeout(fetchFbt, 250);
    }

    // Dispatch countdown
    if (cfg.showDispatchCountdown) {
      updateDispatchCountdown();
      // Update every 30s
      setInterval(updateDispatchCountdown, 30000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---------- Expose a tiny public API ---------- */
  window.mccPdp = {
    setQty: setQty,
    getQty: getQty,
    refresh: function () { updateAll(); }
  };
})();
